import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { array } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { LinkTo } from "@ember/routing";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import getURL from "discourse/lib/get-url";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dFormatDate from "discourse/ui-kit/helpers/d-format-date";
import { i18n } from "discourse-i18n";

export default class ModMarketCard extends Component {
  @service a11y;
  @service currentUser;

  @tracked favoriteOverride;
  @tracked favoriteCountOverride;
  @tracked favoriteSaving = false;

  get favorited() {
    return this.favoriteOverride ?? this.args.mod.favorited === true;
  }

  get favoriteCount() {
    return this.favoriteCountOverride ?? this.args.mod.stats?.favorites ?? 0;
  }

  get favoriteDisabled() {
    return Boolean(
      this.currentUser && !this.args.capabilities?.can_favorite
    );
  }

  get favoriteButtonDisabled() {
    return this.favoriteDisabled || this.favoriteSaving;
  }

  get favoriteLabel() {
    return i18n(
      this.favorited
        ? "discourse_mod_market.actions.unfavorite_with_count"
        : "discourse_mod_market.actions.favorite_with_count",
      { count: this.favoriteCount }
    );
  }

  get favoriteUnavailableTitle() {
    if (!this.favoriteDisabled) {
      return;
    }

    const key =
      {
        missing_external_identity: "discourse_mod_market.favorite.not_linked",
        identity_unavailable:
          "discourse_mod_market.favorite.identity_unavailable",
      }[this.args.capabilities?.favorite_unavailable_reason] ||
      "discourse_mod_market.favorite.unavailable";

    return i18n(key);
  }

  @action
  async toggleFavorite() {
    if (!this.currentUser) {
      getOwner(this).lookup("route:application").send("showLogin");
      return;
    }

    if (this.favoriteButtonDisabled) {
      return;
    }

    const nextFavorited = !this.favorited;
    this.favoriteSaving = true;

    try {
      await ajax(`/mod-market-api/v1/mods/${this.args.mod.id}/favorite`, {
        type: nextFavorited ? "PUT" : "DELETE",
      });
      this.favoriteOverride = nextFavorited;
      this.favoriteCountOverride = Math.max(
        0,
        this.favoriteCount + (nextFavorited ? 1 : -1)
      );
      this.a11y.announce(
        i18n(
          nextFavorited
            ? "discourse_mod_market.favorite.added"
            : "discourse_mod_market.favorite.removed"
        ),
        "polite"
      );
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.favoriteSaving = false;
    }
  }

  <template>
    <article class="mod-market-card" ...attributes>
      <LinkTo
        @route="mod-market-show"
        @models={{array @mod.slug @mod.id}}
        class="mod-market-card__link"
      >
        <div class="mod-market-card__media">
          {{#if @mod.thumbnail_url}}
            <img
              class="mod-market-card__image"
              src={{@mod.thumbnail_url}}
              alt=""
              loading="lazy"
            />
          {{else}}
            <img
              class="mod-market-card__placeholder"
              src={{getURL
                "/plugins/discourse-mod-market/images/mod-market-card-placeholder.png"
              }}
              alt=""
              loading="lazy"
            />
          {{/if}}
          <span class="mod-market-card__type">{{@mod.type_name}}</span>
        </div>
        <h2 class="mod-market-card__title">{{@mod.title}}</h2>
      </LinkTo>

      <div class="mod-market-card__preview">
        <LinkTo
          @route="mod-market-show"
          @models={{array @mod.slug @mod.id}}
          class="mod-market-card__preview-link"
        >
          <div class="mod-market-card__preview-media">
            {{#if @mod.thumbnail_url}}
              <img src={{@mod.thumbnail_url}} alt="" />
            {{else}}
              <img
                src={{getURL
                  "/plugins/discourse-mod-market/images/mod-market-card-placeholder.png"
                }}
                alt=""
              />
            {{/if}}
          </div>
          <div class="mod-market-card__preview-body">
            <h3>{{@mod.title}}</h3>
            <p class="mod-market-card__preview-author">
              {{i18n
                "discourse_mod_market.card.by_author"
                author=@mod.author.name
              }}
              ·
              {{@mod.type_name}}
            </p>
            <p class="mod-market-card__preview-summary">
              {{#if @mod.updated_at}}
                {{dFormatDate @mod.updated_at format="tiny"}}
                {{i18n "discourse_mod_market.card.updated"}}
                ·
              {{/if}}
              {{@mod.description}}
            </p>
          </div>
        </LinkTo>

        <button
          type="button"
          class={{dConcatClass
            "mod-market-card__preview-favorites"
            (if this.favorited "is-selected")
          }}
          disabled={{this.favoriteButtonDisabled}}
          aria-busy={{if this.favoriteSaving "true" "false"}}
          aria-label={{this.favoriteLabel}}
          aria-pressed={{if this.favorited "true" "false"}}
          title={{this.favoriteUnavailableTitle}}
          {{on "click" this.toggleFavorite}}
        >
          <span
            class="mod-market-card__preview-favorite-icon"
            aria-hidden="true"
          ></span>
          <span class="mod-market-card__preview-favorite-count">
            {{this.favoriteCount}}
          </span>
        </button>
      </div>
    </article>
  </template>
}
